import { useCallback, useRef } from 'react'
import axios, { CancelTokenSource } from 'axios'
import { Endpoint, RoleMimetype, getApiUrl } from 'constants/API'
import { VisualFileType } from 'constants/visual'
import { SignedURLDTO, SignedUrlUploadRequestBody } from 'models/visuals'
import { APIRequest } from 'models/API'
import { APIRequestState } from 'constants/API'
import { MINT_RETRY_ATTEMPTS, MINT_RETRY_DELAY_MS, SignedUrlRefresher, uploadToGCResumable } from 'components/common/FileAPI/fileUtils'
import { isRetryableRequestFailure, retryRequestUntilSuccess } from 'utils/API/retryRequestUntilSuccess'
import { getUploadConcurrency } from 'components/common/FileAPI/uploadPool'
import { releaseLease } from 'components/common/FileAPI/uploadLeases'
import { AnalyticsEvent, logAnalyticsEvent } from 'utils/analytics'

import API from 'utils/API/API'
import { auth0ClientModule } from 'utils/auth'
import { DownloadImage, UploadImage, useVisualStore } from 'stores/visualStore'
import { EntityKeys, QueryType } from 'utils/reactQuery'
import { queryClient } from 'utils/reactQuery/queryInit'
import { downloadVisualWorkerType } from 'workers/downloadVisualWorker'
import { wrap } from 'comlink'
import { Auth0Roles } from 'models/auth'
import { logger } from 'utils/logging'

/** Gallery uploads are always resumable; the standard (single-request) uploads of PhotoStudio go through useFileAPI instead. */
type UploadType = 'resumable'

const removeFileExtensionRegex = /\..+/

const VISUAL_MAP = {
  THUMB: {
    [VisualFileType.RAW]: VisualFileType.RAW_THUMB as VisualFileType.RAW_THUMB,
    [VisualFileType.POST]: VisualFileType.POST_THUMB as VisualFileType.POST_THUMB,
  },
  WEB: {
    [VisualFileType.RAW]: VisualFileType.RAW_WEB as VisualFileType.RAW_WEB,
    [VisualFileType.POST]: VisualFileType.POST_WEB as VisualFileType.POST_WEB,
  },
}

// ==========================================
// Download worker pool (module-scoped)
// ==========================================

const initWorkers = () => {
  const workerCount = process.env.NODE_ENV === 'test'
    ? 0
    : navigator.hardwareConcurrency || 4

  const workerPool = [...Array(workerCount).keys()].map(() => {
    const worker = new Worker(
      new URL('workers/downloadVisualWorker', import.meta.url),
      { type: 'module' }
    )
    return wrap<downloadVisualWorkerType>(worker)
  })

  let i = 0

  return async (action: any, token: string, mimetype: RoleMimetype, apiUrl: string) => {
    if (workerCount === 0) return null
    const result = await workerPool[i](action, token, mimetype, apiUrl)
    i = ++i % workerCount
    return result
  }
}

const getWorkDone = initWorkers()

// ==========================================
// React hooks
// ==========================================

export interface UploadVisualParams {
  roles: Auth0Roles
  workspaceId: string
  assignmentId: string
  file: File
  type: Extract<VisualFileType, VisualFileType.RAW | VisualFileType.POST>
  uploadType: UploadType
  replaces?: string
}

export interface DownloadVisualParams {
  roles: Auth0Roles
  workspaceId: string
  assignmentId: string
  filename: string
  type: Exclude<VisualFileType, VisualFileType.RAW | VisualFileType.POST>
  originalFilename?: string
  droppedIn?: number
}

export interface DeleteVisualParams {
  roles: Auth0Roles
  workspaceId: string
  assignmentId: string
  filename: string
  type: Extract<VisualFileType, VisualFileType.RAW | VisualFileType.POST>
}

interface RegisteredUpload {
  source: CancelTokenSource
  abortController: AbortController
  initialData: UploadImage
}

interface PendingUpload {
  params: UploadVisualParams
  registered: RegisteredUpload
}

/** Gives the app-wide upload concurrency bound a denominator, mirroring the CT flow's CT_UPLOAD_V2_TIMED */
interface VisualUploadTimedEvent {
  assignmentId: string
  uploadType: UploadType
  amountOfVisuals: number
  totalSizeB: number
  timeMS: number
}

export function useVisualOperations() {
  const store = useVisualStore()

  const downloadVisual = useCallback(async (params: DownloadVisualParams) => {
    const { roles, workspaceId, assignmentId, filename, type, originalFilename, droppedIn } = params

    if (process.env.NODE_ENV === 'test') return

    // Build the action in the format the worker expects
    const workerAction = {
      type: ['FETCH', 'LOAD_VISUAL'] as [string, string],
      payload: {
        role: roles,
        workspaceId,
        missionId: assignmentId,
        file: new File([], filename),
        type,
        originalFilename,
        droppedIn,
        manipulation: 'DOWNLOAD',
        request: new APIRequest(),
      },
    }

    try {
      const token = await auth0ClientModule.getTokenSilently()
      const mimetype = await API.getRoleMimeType()
      const result = await getWorkDone(workerAction, token, mimetype, getApiUrl())

      if (!result) return

      const downloadData: DownloadImage = {
        file: result.payload.file,
        type,
        signedUrl: result.payload.signedUrl,
        originalFilename,
        request: result.payload.request as DownloadImage['request'],
        lastUpdated: Date.now(),
      }

      store.handleDownloadReceived(assignmentId, filename, type, downloadData)
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const awaitedErrorCodes = new Set([404, 503])
        if (!awaitedErrorCodes.has(error.response?.status || -1)) {
          API.logError(error, { endpoint: Endpoint.VISUAL_DOWNLOAD_URL })
        }
      }
    }
  }, [store])

  /** Pending batch of upload registrations — flushed via queueMicrotask for a single Zustand set() */
  const pendingBatchRef = useRef<Array<{ assignmentId: string, fileName: string, type: VisualFileType, data: UploadImage, originalFilename: string, replaces?: string }>>([])
  const batchScheduledRef = useRef(false)

  /** Register upload entry in the store immediately (before async work begins).
   *  Batches multiple calls into a single Zustand set() via microtask to avoid per-file re-renders. */
  const registerUpload = useCallback((params: UploadVisualParams): RegisteredUpload => {
    const { assignmentId, file, type, replaces } = params
    const source = axios.CancelToken.source()
    // Galleries cancel via the store's CancelTokenSource; the shared uploader speaks AbortSignal
    const abortController = new AbortController()
    source.token.promise.then(() => abortController.abort())
    const isReplacement = !!replaces

    const initialData: UploadImage = {
      file,
      type,
      progress: 0,
      cancelToken: source,
      request: new APIRequest() as APIRequest<null>,
      droppedIn: Date.now(),
      originalFilename: file.name,
      replaces,
    }

    const fileName = isReplacement ? replaces! : file.name
    pendingBatchRef.current.push({ assignmentId, fileName, type, data: initialData, originalFilename: file.name, replaces })

    if (!batchScheduledRef.current) {
      batchScheduledRef.current = true
      queueMicrotask(() => {
        const batch = pendingBatchRef.current
        pendingBatchRef.current = []
        batchScheduledRef.current = false
        store.handleBatchUploadStart(batch)
      })
    }

    return { source, abortController, initialData }
  }, [store])

  /** Uploads one file through the shared FileAPI uploader once its signed URL is known */
  const uploadFile = useCallback(async (params: UploadVisualParams, registered: RegisteredUpload, signedUrlDTO: SignedURLDTO | undefined, refreshSignedUrl?: SignedUrlRefresher, onStart?: () => void) => {
    const { roles, workspaceId, assignmentId, file, type, uploadType, replaces } = params
    const { source, abortController, initialData } = registered
    const isReplacement = !!replaces

    const fileName = isReplacement ? replaces! : file.name

    if (!signedUrlDTO?.signedURL) {
      // The mint committed a placeholder before it came back unusable — release it now rather than waiting out the reaper
      void releaseLease(signedUrlDTO?.leaseId)
      store.setUploadError(assignmentId, fileName, type, 'signedUrl is undefined, null or empty')
      return
    }

    // Parse filename from signed URL
    const split = signedUrlDTO.signedURL.split('?')[0].split('/')
    const parsedFilename = split[split.length - 1]
      .replace(removeFileExtensionRegex, '')
      .replace(/%2B/gi, '+')
    const resolvedFilename = signedUrlDTO.filename || parsedFilename

    // Update store with signed URL and resolved filename
    store.setUploadProgress(assignmentId, resolvedFilename, type, {
      ...initialData,
      signedUrl: signedUrlDTO.signedURL,
      cancelToken: source,
      file: new File([], resolvedFilename),
      originalFilename: file.name,
    })

    // If filename changed (server-assigned), move the upload entry
    if (resolvedFilename !== fileName) {
      store.handleUploadStart(
        assignmentId, resolvedFilename, type,
        { ...initialData, signedUrl: signedUrlDTO.signedURL, cancelToken: source, file: new File([], resolvedFilename), originalFilename: file.name },
        file.name, replaces
      )
    }

    // Step 2: Upload file through the shared uploader (bounded concurrency, resume, verification)
    const onProgress = (percent: number) => {
      store.setUploadProgress(assignmentId, resolvedFilename, type, {
        progress: percent,
        signedUrl: signedUrlDTO.signedURL,
        cancelToken: source,
        file: new File([], resolvedFilename),
        originalFilename: file.name,
        request: new APIRequest(APIRequestState.RUNNING) as APIRequest<null>,
      })
    }

    const onSuccess = () => {
      store.setUploadComplete(assignmentId, resolvedFilename, type, {
        signedUrl: signedUrlDTO.signedURL,
        cancelToken: source,
        file: new File([], resolvedFilename),
        originalFilename: file.name,
      })

      // Invalidate list so FileAPI picks up the new visual
      queryClient.invalidateQueries({ queryKey: [EntityKeys.VISUAL, QueryType.LIST, { assignmentId }] })

      // Step 3: Trigger thumbnail + web download after upload
      const delayMs = isReplacement ? 10000 : 0
      if (type === VisualFileType.RAW || type === VisualFileType.POST) {
        const triggerDownloads = () => {
          downloadVisual({
            roles, workspaceId, assignmentId,
            filename: resolvedFilename,
            type: VISUAL_MAP.THUMB[type],
            originalFilename: file.name,
            droppedIn: Date.now(),
          })
          downloadVisual({
            roles, workspaceId, assignmentId,
            filename: resolvedFilename,
            type: VISUAL_MAP.WEB[type],
            originalFilename: file.name,
            droppedIn: Date.now(),
          })
        }

        if (delayMs > 0) {
          setTimeout(triggerDownloads, delayMs)
        } else {
          triggerDownloads()
        }
      }
    }

    const onError = (error?: string) => {
      store.setUploadError(assignmentId, resolvedFilename, type, error ?? 'Upload failed')
    }

    // Every caller starts this without awaiting it, and the uploaders' own callbacks
    // (onSuccess does store writes, cache invalidation and download fan-out) run outside
    // their try/catch — so without this guard a throw escapes as an unhandled rejection
    // and leaves the store entry stuck RUNNING with no error state and no way to retry.
    try {
      // Cancelled uploads keep their store entry, matching pre-consolidation behavior
      await uploadToGCResumable(file, signedUrlDTO, abortController.signal, onProgress, onSuccess, () => undefined, onError, refreshSignedUrl, undefined, onStart)
    } catch (error) {
      logger.error('Visual upload failed', error, {
        context: {
          component: 'useVisualOperations',
          action: 'upload',
          metadata: { assignmentId, fileName: resolvedFilename, type, uploadType, fileSize: file.size },
        },
      })
      onError(error instanceof Error ? error.message : 'Upload failed')
    }
  }, [store, downloadVisual])

  /** Posts one batch mint call and returns the signed URLs keyed by NFC-normalized filename */
  const postSignedUrlBatch = useCallback(async (entries: PendingUpload[]) => {
    const { assignmentId, uploadType } = entries[0].params
    const url = `${Endpoint.VISUAL_UPLOAD_URL_BATCH.replace('{assignmentId}', encodeURIComponent(assignmentId))}?uploadType=${uploadType.toUpperCase()}`
    const body: SignedUrlUploadRequestBody[] = entries.map(({ params }) => ({
      filename: params.file.name,
      contentType: params.file.type,
      type: params.type,
    }))

    // Per-wave minting gives a transient 429/502/token blip ~N/waveSize chances to land, and the
    // caller abandons the whole remainder on the first one — absorb it here instead.
    const response = await retryRequestUntilSuccess<Record<string, SignedURLDTO>>(
      () => API.post<Record<string, SignedURLDTO>>(url, body, undefined, { endpoint: Endpoint.VISUAL_UPLOAD_URL_BATCH }),
      MINT_RETRY_DELAY_MS,
      MINT_RETRY_ATTEMPTS,
      isRetryableRequestFailure
    )

    // The helper RETURNS the last failure instead of throwing, and a network-level one is not an
    // AxiosError — checking for that alone would let it through as an empty (all-omitted) response.
    if (response instanceof Error) throw response

    // Normalized because the response is keyed by filename and macOS hands over
    // drag-dropped names in NFD: an umlaut round-tripped through the backend as NFC
    // would not match the string we sent, losing the file to an opaque error.
    return new Map(Object.entries(response.data ?? {}).map(([name, dto]) => [name.normalize('NFC'), dto]))
  }, [])

  /** Mints signed URLs for a group of same-assignment uploads one pool-width wave at a time, then starts each upload */
  const mintBatchAndUpload = useCallback(async (entries: PendingUpload[]) => {
    const { assignmentId, uploadType } = entries[0].params
    const startedAt = Date.now()
    const uploads: Promise<unknown>[] = []
    let start = 0

    while (start < entries.length) {
      // Wave = pool width, so a URL is at most one wave old when its file takes a slot and never
      // waits in the queue longer than that (a re-mint would reserve a new blob name and orphan this one).
      // Re-read per wave: the LaunchDarkly `upload-concurrency` flag can move the pool bound
      // mid-drop, and a wave wider than the pool reopens exactly that aging window.
      const wave = entries.slice(start, start + Math.max(1, getUploadConcurrency()))

      let signedUrls: Map<string, SignedURLDTO>
      try {
        signedUrls = await postSignedUrlBatch(wave)
      } catch (error) {
        const errorMessage = axios.isAxiosError(error)
          ? error.response?.data?.message || error.message
          : 'Failed to get upload URL'
        logger.error('Upload signed URL batch failed', error, {
          context: {
            component: 'useVisualOperations',
            action: 'getSignedUrlBatch',
            metadata: { assignmentId, uploadType, fileCount: wave.length, remainingCount: entries.length - start },
          },
        })
        // A backend that refused this wave will refuse the next: settle every remaining file now.
        for (const { params } of entries.slice(start)) {
          store.setUploadError(params.assignmentId, params.file.name, params.type, errorMessage)
        }
        break
      }

      const waveStarts = wave.map((entry) => new Promise<void>((started) => {
        const key = entry.params.file.name.normalize('NFC')
        const signedUrl = signedUrls.get(key)

        // A 200 with fewer keys than files (backend-rejected or same-named file) would
        // otherwise reach the user as a bare "signedUrl is undefined" with no telemetry.
        if (!signedUrl) {
          logger.error('Batch mint returned no signed URL for file', null, {
            context: {
              component: 'useVisualOperations',
              action: 'mintBatchAndUpload',
              metadata: { assignmentId, fileName: entry.params.file.name, mintedCount: signedUrls.size, requestedCount: wave.length },
            },
          })
        }

        const upload = uploadFile(entry.params, entry.registered, signedUrl, async () => (await postSignedUrlBatch([entry])).get(key), started)
        uploads.push(upload)
        // Settling counts as started: a file with no usable URL returns before it ever reaches the pool
        void upload.then(started, started)
      }))
      start += wave.length

      // Mint the next wave only once every file of this one has taken a pool slot. Its URLs then
      // wait at most a pool width of completions before their files initiate while this drop is the
      // pool's sole user; each concurrent drop on the app-wide `runInUploadPool` (the CT upload page,
      // thread attachments, PhotoStudio masks) adds another pool width. Gating on a wave draining
      // instead let one stalled file freeze the drop (QA-460).
      await Promise.all(waveStarts)
    }

    await Promise.allSettled(uploads)

    logAnalyticsEvent<VisualUploadTimedEvent>(AnalyticsEvent.GALLERY_UPLOAD_TIMED, {
      assignmentId,
      uploadType,
      amountOfVisuals: entries.length,
      totalSizeB: entries.reduce((sum, { params }) => sum + params.file.size, 0),
      timeMS: Date.now() - startedAt,
    })
  }, [store, uploadFile, postSignedUrlBatch])

  /** Mints a signed URL for a single replacement upload (the batch endpoint does not handle replacements) */
  const mintReplacementAndUpload = useCallback(async ({ params, registered }: PendingUpload) => {
    const { assignmentId, file, type, uploadType, replaces } = params
    const url = `${Endpoint.VISUAL_UPLOAD_URL_REPLACE.replace('{assignmentId}', encodeURIComponent(assignmentId))}?uploadType=${uploadType.toUpperCase()}`
    const body: SignedUrlUploadRequestBody = { filename: replaces!, replacementFilename: file.name, contentType: file.type, type }

    const mint = async () => (await API.put<SignedURLDTO>(url, body, undefined, {
      endpoint: Endpoint.VISUAL_UPLOAD_URL_REPLACE,
    })).data

    try {
      await uploadFile(params, registered, await mint(), mint)
    } catch (error) {
      const errorMessage = axios.isAxiosError(error)
        ? error.response?.data?.message || error.message
        : 'Failed to get upload URL'
      logger.error('Upload signed URL failed', error, { context: { component: 'useVisualOperations', action: 'getSignedUrl' } })
      store.setUploadError(assignmentId, replaces!, type, errorMessage)
    }
  }, [store, uploadFile])

  /** Pending uploads awaiting signed URLs — flushed via microtask so files dropped together
   *  mint through a single batch call. Upload concurrency is bounded in the shared uploader. */
  const pendingMintsRef = useRef<PendingUpload[]>([])
  const mintScheduledRef = useRef(false)

  const queueUpload = useCallback((params: UploadVisualParams) => {
    pendingMintsRef.current.push({ params, registered: registerUpload(params) })

    if (!mintScheduledRef.current) {
      mintScheduledRef.current = true
      queueMicrotask(() => {
        const batch = pendingMintsRef.current
        pendingMintsRef.current = []
        mintScheduledRef.current = false

        // Nothing awaits these, so each needs its own handler — a bare rejection here
        // surfaces as a context-free unhandledrejection in Sentry.
        const reportFlushFailure = (action: string) => (error: unknown) =>
          logger.error('Upload flush failed', error, { context: { component: 'useVisualOperations', action } })

        for (const entry of batch) {
          if (entry.params.replaces) mintReplacementAndUpload(entry).catch(reportFlushFailure('mintReplacementAndUpload'))
        }

        const groups = new Map<string, PendingUpload[]>()
        for (const entry of batch) {
          if (entry.params.replaces) continue
          const key = `${entry.params.assignmentId}|${entry.params.uploadType}`
          groups.set(key, [...(groups.get(key) ?? []), entry])
        }
        groups.forEach((group) => mintBatchAndUpload(group).catch(reportFlushFailure('mintBatchAndUpload')))
      })
    }
  }, [registerUpload, mintReplacementAndUpload, mintBatchAndUpload])

  const deleteVisual = useCallback(async (params: DeleteVisualParams) => {
    const { roles, workspaceId, assignmentId, filename, type } = params

    // Cancel ongoing upload
    const cancelToken = store.getUploadCancelToken(assignmentId, filename, type)
    if (cancelToken) cancelToken.cancel()

    // Mark as deleting in store
    store.startDelete(assignmentId, filename, type)

    // API call
    const url = roles.isClient
      ? Endpoint.VISUAL_DELETE_WITH_WORKSPACE
        .replace('{workspaceId}', encodeURIComponent(workspaceId))
        .replace('{assignmentId}', encodeURIComponent(assignmentId))
        .replace('{filename}', encodeURIComponent(filename))
      : Endpoint.VISUAL_DELETE
        .replace('{assignmentId}', encodeURIComponent(assignmentId))
        .replace('{filename}', encodeURIComponent(filename))

    try {
      await API.delete<null>(url, {
        params: { type: type.toString() },
      }, {
        endpoint: roles.isClient ? Endpoint.VISUAL_DELETE_WITH_WORKSPACE : Endpoint.VISUAL_DELETE,
      })

      // Complete delete in store
      store.completeDelete(assignmentId, filename, type)
      // Also clean up upload entry (may be under a different key with extension)
      store.removeUploadWrapper(assignmentId, filename)

      // Invalidate React Query visual list cache
      queryClient.invalidateQueries({ queryKey: [EntityKeys.VISUAL, QueryType.LIST, { assignmentId }] })
    } catch (error) {
      logger.error('Visual delete failed', error, { context: { component: 'useVisualOperations', action: 'delete', metadata: { assignmentId, filename, type } } })
      // The blob is still there: put the tile back and let the server listing confirm it
      store.failDelete(assignmentId, filename, type)
      queryClient.invalidateQueries({ queryKey: [EntityKeys.VISUAL, QueryType.LIST, { assignmentId }] })
    }
  }, [store])

  return {
    uploadVisual: queueUpload,
    uploadVisualReplacement: (params: UploadVisualParams) => queueUpload(params),
    downloadVisual,
    deleteVisual,
  }
}
